/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/require-default-props */
import { faChevronLeft, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, ListGroup, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { QUESTION_VALUE_TYPE } from '../../../../constants/common';
import useAutoSave from '../../hooks/useAutoSave';
import TagList from './TagList';
import '../QuestionEditForm/styles.css';

// Multi-dimension samples keep one response label per dimension; everything
// else keeps them in the first dimension's allowedValues.
const isLabelMode = (sample) =>
  [QUESTION_VALUE_TYPE.MULTIPLE, QUESTION_VALUE_TYPE.GRID].includes(sample?.Data.valueType);

// The attribute-filter editor. Mirrors QuestionEditForm's state handling for
// the filter text and response labels ("tags"); the filter category is shown
// but locked to Attribute (editable in a future ticket).
const FilterEditForm = ({
  sample,
  dimensions,
  busy,
  busyLabel,
  onClose,
  onSave,
  onSaved,
  onPreviewChange,
}) => {
  const [prompt, setPrompt] = useState('');
  const [tags, setTags] = useState([]);
  const [hiddenTags, setHiddenTags] = useState([]);
  const [tagIdOrders, setTagIdOrders] = useState([]);

  const labelMode = isLabelMode(sample);

  // Initialize the form from the loaded sample + dimensions, same derivation
  // QuestionEditForm uses for each value type.
  useEffect(() => {
    if (!sample || !dimensions) return;
    if (isLabelMode(sample)) {
      setPrompt(sample.Data.text);
      const labelList = dimensions
        .filter((dimension) => dimension.Data.valueType !== QUESTION_VALUE_TYPE.OPEN_TEXT)
        .map((dimension) => ({ id: dimension.Id, value: dimension.Data.label }));
      setTags(labelList);
      // hiddenAnswers must hold strings (the backend validates the items as
      // strings), while label-mode tag ids are numeric dimension ids — keep
      // the state stringified and compare with String() coercion.
      setHiddenTags((sample.Data.hiddenAnswers || []).map(String));
      setTagIdOrders(
        sample.Data.labelOrders && sample.Data.labelOrders.length > 0
          ? sample.Data.labelOrders
          : labelList.map((label) => label.id)
      );
    } else {
      setPrompt(sample.Data.label || sample.Data.text);
      const { allowedValues } = dimensions[0].Data;
      const answerList = Object.keys(allowedValues || {})
        .filter((key) => key >= 0)
        .map((key) => ({ id: key, value: allowedValues[key] }));
      setTags(answerList);
      setHiddenTags((sample.Data.hiddenAnswers || []).map(String));
      setTagIdOrders(
        sample.Data.answerOrders && sample.Data.answerOrders.length > 0
          ? sample.Data.answerOrders
          : answerList.map((answer) => answer.id)
      );
    }
  }, [sample, dimensions]);

  // Mirror the tag state up so the overview's expanded row follows edits
  // (reorders, hides, renames) live, without waiting for the auto-save.
  useEffect(() => {
    if (!onPreviewChange) return;
    onPreviewChange(tags.length > 0 ? { tags, tagIdOrders, hiddenTags } : null);
  }, [tags, tagIdOrders, hiddenTags, onPreviewChange]);

  const onFilterTextChange = (e) => {
    setPrompt(e.target.value);
  };

  // Tags render in tagIdOrders order, so the row index does not match the
  // tags array after a reorder — update by the input's id instead.
  const onTagChange = (e) => {
    const { id, value } = e.target;
    setTags((current) =>
      current.map((tag) => (String(tag.id) === String(id) ? { ...tag, value } : tag))
    );
  };

  // Folds the edited tags and filter text back into copies of the dimension
  // objects, following the same shapes QuestionEditForm persists.
  const buildUpdatedDimensions = useCallback(() => {
    if (!dimensions) return [];
    if (labelMode) {
      const labelMap = tags.reduce((accumulator, tag) => {
        accumulator[tag.id] = tag.value;
        return accumulator;
      }, {});
      return dimensions.map((dimension) =>
        labelMap[dimension.Id] !== undefined
          ? {
              ...dimension,
              Data: { ...dimension.Data, label: labelMap[dimension.Id], text: prompt },
            }
          : dimension
      );
    }
    const answersMap = tags.reduce((accumulator, tag) => {
      accumulator[tag.id] = tag.value;
      return accumulator;
    }, {});
    const newAllowed = { ...dimensions[0].Data.allowedValues, ...answersMap };
    return [
      { ...dimensions[0], Data: { ...dimensions[0].Data, allowedValues: newAllowed } },
      ...dimensions.slice(1),
    ];
  }, [dimensions, labelMode, tags, prompt]);

  const handleSave = useCallback(async () => {
    if (!sample) return;
    const samplePatch = {
      Id: sample.Id,
      Data: labelMode
        ? { label: prompt, text: prompt, hiddenAnswers: hiddenTags, labelOrders: tagIdOrders }
        : { label: prompt, text: prompt, hiddenAnswers: hiddenTags, answerOrders: tagIdOrders },
    };
    await onSave({
      samplePatch,
      updatedDimensions: buildUpdatedDimensions(),
      surveyId: sample.Parent,
    });
    onSaved();
  }, [sample, labelMode, prompt, hiddenTags, tagIdOrders, buildUpdatedDimensions, onSave, onSaved]);

  const autoSave = useAutoSave(handleSave);
  const { markDirty } = autoSave;

  const onHiddenChange = (hiddenList) => {
    setHiddenTags(hiddenList);
  };
  const onTagIdOrderChange = (idOrders) => {
    setTagIdOrders([...idOrders]);
  };

  return (
    <Col md="3" className="p-3 survey-sidebar edit-mode position-relative">
      {busy && (
        <div className="editor-loading-overlay">
          <Spinner animation="border" role="status" />
          {busyLabel}
        </div>
      )}
      <span className="d-flex" onClick={onClose} onKeyDown={onClose} role="button" tabIndex={0}>
        <FontAwesomeIcon size="sm" className="mr-3" icon={faChevronLeft} />
        Back to overview
      </span>
      <Col className="info-edit-form edit-body">
        <div>
          <h4>Filter text</h4>
          <Form.Control
            as="textarea"
            className="mt-3"
            value={prompt}
            onChange={onFilterTextChange}
            onBlur={markDirty}
            style={{ height: '100px' }}
            maxLength={500}
            placeholder="Filter text"
          />
        </div>
        <TagList
          tags={tags}
          tagIdOrders={tagIdOrders}
          hiddenTags={hiddenTags}
          onTagChange={onTagChange}
          onHiddenChange={onHiddenChange}
          onTagIdOrderChange={onTagIdOrderChange}
          onDirty={markDirty}
        />
        <div className="border-gray mt-3" />
        <h4 className="mb-3">
          Filter Category
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                The filter category is locked for attribute filters. Editing it will be enabled in a
                future release.
              </Tooltip>
            }
            delay={100}
          >
            <FontAwesomeIcon className="cursor-pointer ml-2" size="1x" icon={faInfoCircle} />
          </OverlayTrigger>
        </h4>
        <ListGroup>
          <ListGroup.Item>
            <Form.Check
              type="radio"
              checked
              disabled
              readOnly
              id="filter-attribute"
              label="Attribute"
              value="ATTRIBUTE"
            />
          </ListGroup.Item>
        </ListGroup>
      </Col>
      <Button
        variant="primary"
        onClick={autoSave.saveNow}
        disabled={busy}
        className="btn btn-custom dark mt-3"
      >
        Save
      </Button>
    </Col>
  );
};

FilterEditForm.propTypes = {
  sample: PropTypes.instanceOf(Object),
  dimensions: PropTypes.arrayOf(PropTypes.object),
  busy: PropTypes.bool,
  busyLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onPreviewChange: PropTypes.func,
};

export default FilterEditForm;
